import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatPrice, getNextTierSavings } from '@/lib/pricing';
import { validateDiscountCode as checkDiscountCode, applyDiscountCode } from '@/lib/pricing';
import useCartStore from '@/store/cartStore';
import { useShallow } from 'zustand/react/shallow';
import { Tag, X, CheckCircle2, AlertCircle, Sparkles, TrendingUp, Gift } from 'lucide-react';
import productData from '@/data/products';

export function OrderSummary() {
  const [discountInput, setDiscountInput] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [discountSuccess, setDiscountSuccess] = useState('');
  
  const items = useCartStore((state) => state.items);
  const applyDiscount = useCartStore((state) => state.applyDiscountCode);
  const removeDiscount = useCartStore((state) => state.removeDiscountCode);

  // 🛡️ 最終修復：扁平化選取 (Flattened Selection)
  // 將所有嵌套物件拆解成原始值，確保 useShallow 能正確運作
  const cartValues = useCartStore(
    useShallow((state) => {
      const s = state.getCartSummary();
      return {
        totalItems: s.totalItems,
        subtotal: s.subtotal,
        bulkDiscountAmount: s.bulkDiscountAmount,
        shipping: s.shipping,
        total: s.total,
        totalSavings: s.totalSavings,
        discountCodeAmount: s.discountCodeAmount,
        // 拆解 bulkDiscountInfo
        bulkApplicable: s.bulkDiscountInfo?.applicable,
        bulkMessage: s.bulkDiscountInfo?.message,
        bulkRate: s.bulkDiscountInfo?.rate,
        // 拆解 discountCode
        discountCodeValue: s.discountCode?.code,
      };
    })
  );

  // 🔄 資料重組：將扁平化的資料組裝回原本的 summary 結構
  // 這樣下方的 JSX 就完全不需要修改，保持邏輯一致
  const summary = {
    totalItems: cartValues.totalItems,
    subtotal: cartValues.subtotal,
    bulkDiscountAmount: cartValues.bulkDiscountAmount,
    shipping: cartValues.shipping,
    total: cartValues.total,
    totalSavings: cartValues.totalSavings,
    discountCodeAmount: cartValues.discountCodeAmount,
    bulkDiscountInfo: {
      applicable: cartValues.bulkApplicable,
      message: cartValues.bulkMessage,
      rate: cartValues.bulkRate,
    },
    discountCode: cartValues.discountCodeValue ? { code: cartValues.discountCodeValue } : null,
  };

  const handleApplyDiscount = () => {
    setDiscountError('');
    setDiscountSuccess('');
    
    if (!discountInput.trim()) {
      setDiscountError('請輸入折扣碼');
      return;
    }

    const validation = checkDiscountCode(discountInput, summary.totalItems);
    
    if (!validation.valid) {
      setDiscountError(validation.error);
      return;
    }

    // 計算折扣後的總金額（團購折扣後）
    const afterBulkDiscount = summary.subtotal - summary.bulkDiscountAmount;
    const result = applyDiscountCode(discountInput, afterBulkDiscount, summary.totalItems);

    if (result.success) {
      applyDiscount(result.code, result.amount, result.type);
      setDiscountSuccess(`已套用折扣碼：${result.message}`);
      setDiscountInput('');
    } else {
      setDiscountError(result.message);
    }
  };

  const handleRemoveDiscount = () => {
    removeDiscount();
    setDiscountSuccess('');
    setDiscountError('');
  };

  // 計算下一級優惠可以省下的金額
  const nextTierInfo = getNextTierSavings(
    summary.totalItems,
    productData.pricing.salePrice,
    productData.bulkDiscounts
  );

  return (
    <Card className="sticky top-4">
      <CardHeader>
        <CardTitle>訂單摘要</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 商品列表 */}
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="flex gap-3">
              <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                <img
                  src={item.image || '/placeholder.jpg'}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{item.shortName}</p>
                <p className="text-sm text-gray-500">
                  {formatPrice(item.price)} × {item.quantity}
                </p>
                <p className="text-sm font-medium">
                  {formatPrice(item.price * item.quantity)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <Separator />

        {/* 折扣碼輸入 */}
        <div className="space-y-2">
          <Label htmlFor="discount_code">折扣碼</Label>
          
          {summary.discountCode ? (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <Tag className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-800 flex-1">
                {summary.discountCode.code}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveDiscount}
                className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                id="discount_code"
                placeholder="輸入折扣碼"
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApplyDiscount();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleApplyDiscount}
              >
                套用
              </Button>
            </div>
          )}
          
          {discountError && (
            <Alert variant="destructive" className="py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {discountError}
              </AlertDescription>
            </Alert>
          )}
          
          {discountSuccess && (
            <Alert className="py-2 border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-sm text-green-800">
                {discountSuccess}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <Separator />

        {/* 價格明細 */}
        <div className="space-y-3">
          {/* 小計 */}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">小計</span>
            <span className="font-medium">{formatPrice(summary.subtotal)}</span>
          </div>

          {/* 團購優惠 */}
          {summary.bulkDiscountInfo.applicable && summary.bulkDiscountAmount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <div className="flex items-center gap-1">
                <Sparkles className="h-4 w-4" />
                <span>團購優惠（{summary.bulkDiscountInfo.message}）</span>
              </div>
              <span className="font-medium">-{formatPrice(summary.bulkDiscountAmount)}</span>
            </div>
          )}

          {/* 折扣碼優惠 */}
          {summary.discountCodeAmount > 0 && (
            <div className="flex justify-between text-sm text-blue-600">
              <div className="flex items-center gap-1">
                <Tag className="h-4 w-4" />
                <span>折扣碼</span>
              </div>
              <span className="font-medium">-{formatPrice(summary.discountCodeAmount)}</span>
            </div>
          )}

          {/* 運費 */}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">運費</span>
            <span className={summary.shipping === 0 ? 'text-green-600 font-medium' : ''}>
              {summary.shipping === 0 ? '免運費' : formatPrice(summary.shipping)}
            </span>
          </div>

          <Separator />

          {/* 總計 */}
          <div className="flex justify-between items-baseline">
            <span className="text-lg font-semibold">總計</span>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {formatPrice(summary.total)}
              </div>
              {summary.totalSavings > 0 && (
                <div className="text-xs text-green-600">
                  已省 {formatPrice(summary.totalSavings)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 下一個優惠門檻提示 - 增強版 */}
        {nextTierInfo && (
          <Alert className="border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50">
            <TrendingUp className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-sm">
              <div className="space-y-1">
                <p className="font-semibold text-orange-900">
                  🎁 再加購 {nextTierInfo.needToBuy} 件，享更多優惠！
                </p>
                <p className="text-orange-800">
                  升級至 {nextTierInfo.nextThreshold} 件享 <strong>{Math.round(nextTierInfo.nextRate * 10)} 折</strong>，
                  <strong className="text-orange-600"> 可再省 {formatPrice(nextTierInfo.savings)}</strong>
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* 已享有最高優惠提示 */}
        {!nextTierInfo && summary.bulkDiscountInfo.applicable && (
          <Alert className="border-green-200 bg-green-50">
            <Gift className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-sm text-green-800">
              🎉 恭喜！您已享有最高團購優惠（{Math.round(summary.bulkDiscountInfo.rate * 10)} 折），省下 {formatPrice(summary.bulkDiscountAmount)}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
